import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import { PROJECTS as PID } from "../constants/testIds";

const FALLBACK = [
  {
    slug: "the-azure-residences",
    name: "The Azure Residences",
    location: "Patia, Bhubaneswar",
    typology: "3 & 4 BHK Sky Residences",
    status: "ongoing",
    year: 2026,
    hero_image: "https://images.unsplash.com/photo-1614595737476-42487331b8a1?auto=format&fit=crop&w=1600&q=85",
    description: "A tranquil residential landmark rising above the city — Azure blends architectural clarity with layered greenery, oversized balconies, and sunlit interiors designed for a slower, more intentional life.",
    rera: "RP/19/2025/AZR-1401",
  },
  {
    slug: "kliving-plaza",
    name: "KLiving Plaza",
    location: "Kalinga Nagar, Bhubaneswar",
    typology: "Grade-A Commercial Tower",
    status: "ongoing",
    year: 2027,
    hero_image: "https://images.unsplash.com/photo-1619218070141-bcfeb8b93074?auto=format&fit=crop&w=1600&q=85",
    description: "A commercial address that refuses to be ordinary — pre-certified green architecture, double-height retail lobbies and a public forecourt designed for the city, not just the tenants.",
    rera: "RP/19/2025/PLZ-2207",
  },
  {
    slug: "the-meridian-exchange",
    name: "The Meridian Exchange",
    location: "Chandrasekharpur, Bhubaneswar",
    typology: "Mixed-use Landmark",
    status: "upcoming",
    year: 2028,
    hero_image: "https://images.unsplash.com/photo-1558661091-5cc1b64d0dc5?auto=format&fit=crop&w=1600&q=85",
    description: "A civic-scale mixed-use development anchored by a public plaza — a place where residences, workplaces and cultural venues share the same address and the same light.",
    rera: "RP/19/2025/MRD-3110",
  },
  {
    slug: "the-cove-villas",
    name: "The Cove Villas",
    location: "Jatni, Bhubaneswar",
    typology: "Boutique Villas · Completed 2024",
    status: "completed",
    year: 2024,
    hero_image: "https://images.unsplash.com/photo-1613545325268-9265e1609167?auto=format&fit=crop&w=1600&q=85",
    description: "Twelve boutique villas around a central lily-pond courtyard — completed 2024, now home to a small community of collectors, doctors and creators.",
    rera: "RP/19/2022/COV-0912",
  },
];

export default function FeaturedProjects({ projects }) {
  // Fix: Explicitly check if it's a valid array before attempting layout mapping
  const source = Array.isArray(projects) && projects.length ? projects : FALLBACK;
  
  const items = source.slice(0, 4).map((p) => ({
    ...p,
    description: p.description || "",
    rera: p.rera || `RP/19/${p.year || 2025}/KL-${(p.slug || "").slice(0, 3).toUpperCase()}`,
  }));

  const [active, setActive] = useState(0);
  
  // Guard clause against empty items array to prevent undefined runtime errors
  const p = items[active] || FALLBACK[0];
  
  const prev = () => setActive((i) => (i - 1 + items.length) % items.length);
  const next = () => setActive((i) => (i + 1) % items.length);

  return (
    <section
      id="projects"
      data-testid={PID.section}
      className="relative bg-surface text-obsidian py-24 md:py-32 px-6 md:px-10 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <div className="chapter-num text-emerald_kl mb-6">
              <span className="inline-block w-8 h-px bg-emerald_kl/60 align-middle mr-3" />
              PORTFOLIO · SELECTED WORKS
            </div>
            <h2 className="font-serif-display leading-[0.95] tracking-tight text-obsidian text-5xl md:text-6xl lg:text-[88px]">
              Featured <span className="italic text-emerald_kl">Projects</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right flex md:justify-end items-center gap-3">
            <div className="chapter-num text-obsidian/50 mr-4 hidden md:block">
              {String(active + 1).padStart(2, "0")} <span className="text-obsidian/30">/ {String(items.length).padStart(2, "0")}</span>
            </div>
            <button
              onClick={prev}
              className="w-11 h-11 grid place-items-center border border-obsidian/25 text-obsidian hover:bg-emerald_kl hover:text-editorial hover:border-emerald_kl transition-colors duration-300"
              aria-label="Previous project"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              className="w-11 h-11 grid place-items-center border border-obsidian/25 text-obsidian hover:bg-emerald_kl hover:text-editorial hover:border-emerald_kl transition-colors duration-300"
              aria-label="Next project"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Detail panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={p.slug}
            data-testid={PID.card(p.slug)}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-12 items-stretch"
          >
            {/* Left: big image */}
            <div className="col-span-12 md:col-span-7 relative">
              <div className="aspect-[16/11] md:aspect-[4/3] overflow-hidden bg-obsidian/5">
                <motion.img
                  key={p.hero_image}
                  src={p.hero_image}
                  alt={p.name}
                  initial={{ scale: 1.08 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="chapter-num text-editorial bg-obsidian/75 backdrop-blur-md px-2.5 py-1.5">
                  {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                </span>
                <span className="chapter-num text-obsidian bg-lime_kl px-2.5 py-1.5">{p.status?.toUpperCase()}</span>
              </div>
            </div>

            {/* Right: description panel */}
            <div className="col-span-12 md:col-span-5 flex flex-col justify-between md:pl-4">
              <div>
                <div className="chapter-num text-emerald_kl mb-4">{p.typology?.toUpperCase()}</div>
                <h3 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-obsidian">
                  {p.name}
                </h3>
                <p className="mt-6 text-obsidian/75 text-base md:text-lg leading-relaxed max-w-md">
                  {p.description}
                </p>
              </div>

              <div className="mt-10 space-y-6 border-t border-obsidian/15 pt-8">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="chapter-num text-obsidian/50 mb-2">LOCATION</div>
                    <div className="flex items-start gap-2 text-obsidian text-sm">
                      <MapPin className="w-4 h-4 mt-0.5 text-emerald_kl shrink-0" />
                      <span>{p.location}</span>
                    </div>
                  </div>
                  <div>
                    <div className="chapter-num text-obsidian/50 mb-2">HANDOVER</div>
                    <div className="text-obsidian text-sm">{p.year}</div>
                  </div>
                </div>

                <div>
                  <div className="chapter-num text-obsidian/50 mb-2">ORERA REGISTERED NO.</div>
                  <div className="font-mono-kl text-obsidian text-sm">{p.rera}</div>
                </div>

                <a
                  href="#contact"
                  className="btn-kl inline-flex mt-2 bg-obsidian text-editorial border-obsidian hover:bg-emerald_kl hover:border-emerald_kl"
                >
                  Read More <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Project dots + progress */}
        <div className="mt-14 flex items-center gap-6">
          <div className="flex items-center gap-3">
            {items.map((it, i) => (
              <button
                key={it.slug}
                onClick={() => setActive(i)}
                aria-label={`Go to ${it.name}`}
                className={`h-1.5 transition-all duration-500 ${
                  i === active ? "w-10 bg-emerald_kl" : "w-6 bg-obsidian/20 hover:bg-obsidian/40"
                }`}
              />
            ))}
          </div>
          <div className="chapter-num text-obsidian/50 hidden md:block">
            {items[active]?.name?.toUpperCase()}
          </div>
        </div>
      </div>
    </section>
  );
}